/**
 * ChunkedUploadService
 * 대용량 파일을 청크로 분할하여 Cloud Run을 통해 NAS에 업로드
 */

const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB

interface ChunkedUploadProgress {
  uploadedChunks: number;
  totalChunks: number;
  percentage: number;
  status: 'initializing' | 'uploading' | 'finalizing' | 'completed' | 'error';
  message?: string;
}

interface InitResponse {
  success: boolean;
  sessionId: string;
  message: string;
}

interface ChunkResponse {
  success: boolean;
  uploadedChunks: number;
  totalChunks: number;
}

interface FinalizeResponse {
  success: boolean;
  message: string;
  nasPath: string;
}

export class ChunkedUploadService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = process.env.REACT_APP_NAS_PROXY_URL || '';
    if (!this.baseUrl) {
      console.warn('⚠️ REACT_APP_NAS_PROXY_URL not configured');
    }
  }

  /**
   * 파일을 청크로 분할
   */
  private splitFileIntoChunks(file: File): Blob[] {
    const chunks: Blob[] = [];
    let offset = 0;

    while (offset < file.size) {
      const chunk = file.slice(offset, offset + CHUNK_SIZE);
      chunks.push(chunk);
      offset += CHUNK_SIZE;
    }

    return chunks;
  }

  /**
   * Blob을 Base64로 변환
   */
  private async blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        // "data:...;base64," 제거
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * 업로드 세션 초기화
   */
  private async initUploadSession(
    fileName: string,
    totalChunks: number,
    nasPath: string
  ): Promise<string> {
    const response = await fetch(`${this.baseUrl}/api/chunked/init`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        fileName,
        totalChunks,
        nasPath
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to initialize upload session');
    }

    const data: InitResponse = await response.json();
    return data.sessionId;
  }

  /**
   * 청크 업로드
   */
  private async uploadChunk(
    sessionId: string,
    chunkIndex: number,
    chunk: Blob
  ): Promise<ChunkResponse> {
    const base64Chunk = await this.blobToBase64(chunk);

    const response = await fetch(`${this.baseUrl}/api/chunked/chunk`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        sessionId,
        chunkIndex,
        chunk: base64Chunk
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to upload chunk');
    }

    return await response.json();
  }

  /**
   * 업로드 완료 처리
   */
  private async finalizeUpload(sessionId: string): Promise<FinalizeResponse> {
    const response = await fetch(`${this.baseUrl}/api/chunked/finalize`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ sessionId })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to finalize upload');
    }

    return await response.json();
  }

  /**
   * 대용량 파일 업로드 (메인 메서드)
   */
  public async uploadLargeFile(
    file: File,
    nasPath: string,
    onProgress?: (progress: ChunkedUploadProgress) => void
  ): Promise<string> {
    try {
      // 1. 파일 분할
      const chunks = this.splitFileIntoChunks(file);
      const totalChunks = chunks.length;

      console.log(`📦 File split into ${totalChunks} chunks`);

      // Progress 업데이트
      onProgress?.({
        uploadedChunks: 0,
        totalChunks,
        percentage: 0,
        status: 'initializing',
        message: '업로드 세션 초기화 중...'
      });

      // 2. 세션 초기화
      const sessionId = await this.initUploadSession(file.name, totalChunks, nasPath);
      console.log(`✅ Session created: ${sessionId}`);

      onProgress?.({
        uploadedChunks: 0,
        totalChunks,
        percentage: 0,
        status: 'uploading',
        message: '청크 업로드 중...'
      });

      // 3. 청크 순차 업로드
      for (let i = 0; i < totalChunks; i++) {
        await this.uploadChunk(sessionId, i, chunks[i]);

        const uploadedChunks = i + 1;
        const percentage = Math.round((uploadedChunks / totalChunks) * 100);

        console.log(`✅ Chunk ${uploadedChunks}/${totalChunks} uploaded (${percentage}%)`);

        onProgress?.({
          uploadedChunks,
          totalChunks,
          percentage,
          status: 'uploading',
          message: `청크 ${uploadedChunks}/${totalChunks} 업로드 완료`
        });
      }

      // 4. 최종 처리
      onProgress?.({
        uploadedChunks: totalChunks,
        totalChunks,
        percentage: 100,
        status: 'finalizing',
        message: '파일 조립 및 NAS 저장 중...'
      });

      const result = await this.finalizeUpload(sessionId);
      console.log(`✅ Upload completed: ${result.nasPath}`);

      onProgress?.({
        uploadedChunks: totalChunks,
        totalChunks,
        percentage: 100,
        status: 'completed',
        message: '업로드 완료!'
      });

      return result.nasPath;

    } catch (error: any) {
      console.error('❌ Chunked upload failed:', error);

      onProgress?.({
        uploadedChunks: 0,
        totalChunks: 0,
        percentage: 0,
        status: 'error',
        message: error.message || '업로드 실패'
      });

      throw error;
    }
  }
}

export default new ChunkedUploadService();
